import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/global.css';
import './styles/variables.css';
import './styles/components.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Initialise internationalisation (must run before any component uses useTranslation).
import './i18n';

// Polyfills for older browsers
import 'core-js/stable';
import 'regenerator-runtime/runtime';

//Adding React Query Provider
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./config/queryClient";

// Per-route document head (SEO) provider
import { HelmetProvider } from "react-helmet-async";

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Application error:', error, errorInfo);
    this.setState({ error, errorInfo });
    
    // Log to error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Send to your error tracking service (Sentry, LogRocket, etc.)
      // logErrorToService(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#f5f5f5'
        }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#d32f2f' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', color: '#666' }}>
            We're sorry, but an unexpected error occurred. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              fontSize: '1rem',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.3s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
          >
            Refresh Page
          </button>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div style={{ marginTop: '2rem', textAlign: 'left' }}>
              <h3 style={{ color: '#d32f2f' }}>Error Details (Development Only):</h3>
              <pre style={{
                padding: '1rem',
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                borderRadius: '4px',
                overflow: 'auto',
                maxWidth: '800px'
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading fallback
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '3px solid #f3f3f3',
        borderTop: '3px solid #1976d2',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 20px'
      }} />
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Vitrine LMS...</p>
    </div>
  </div>
);

// Check if browser supports required features
const checkBrowserCompatibility = () => {
  const features = {
    'Promise': typeof Promise !== 'undefined',
    'fetch': typeof fetch !== 'undefined',
    'Symbol': typeof Symbol !== 'undefined',
    'Object.assign': typeof Object.assign === 'function',
    'Array.from': typeof Array.from === 'function',
    'Map': typeof Map !== 'undefined',
    'Set': typeof Set !== 'undefined',
  };

  const missingFeatures = Object.entries(features)
    .filter(([, supported]) => !supported)
    .map(([feature]) => feature);

  if (missingFeatures.length > 0) {
    console.warn('Browser missing features:', missingFeatures);
    return false;
  }

  return true;
};

// Service worker registration + new-version handling now live in the app
// (components/ui/UpdateBanner + utils/appUpdate): a new deploy prompts the user
// to "Update" (installed app) or "Refresh" (browser) instead of silently
// serving a stale cached build.

// Set up global error handlers
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  // Prevent default browser error logging
  event.preventDefault();
});

// Prerendered (postbuild) public pages ship rendered HTML inside #root, so we
// HYDRATE that markup when present; otherwise we create a fresh root. This keeps
// per-page content + <head> meta in the static HTML (for crawlers / AI / social
// scrapers) while the app still becomes fully interactive.
const container = document.getElementById("root");
let root;

if (!checkBrowserCompatibility()) {
  // Render fallback for unsupported browsers
  root = ReactDOM.createRoot(container);
  root.render(
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      textAlign: 'center',
      fontFamily: 'Arial, sans-serif'
    }}>
      <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#d32f2f' }}>
        Browser Not Supported
      </h1>
      <p style={{ fontSize: '1.1rem', marginBottom: '2rem', color: '#666', maxWidth: '600px' }}>
        Your browser is not supported. Please upgrade to a modern browser like Chrome, Firefox, Safari, or Edge to use Vitrine LMS.
      </p>
      <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <a href="https://www.google.com/chrome" target="_blank" rel="noopener noreferrer" style={{
          padding: '10px 20px',
          backgroundColor: '#4285F4',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '4px'
        }}>Chrome</a>
        <a href="https://www.mozilla.org/firefox" target="_blank" rel="noopener noreferrer" style={{
          padding: '10px 20px',
          backgroundColor: '#FF7139',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '4px'
        }}>Firefox</a>
        <a href="https://www.microsoft.com/edge" target="_blank" rel="noopener noreferrer" style={{
          padding: '10px 20px',
          backgroundColor: '#0078D7',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '4px'
        }}>Edge</a>
        <a href="https://www.apple.com/safari" target="_blank" rel="noopener noreferrer" style={{
          padding: '10px 20px',
          backgroundColor: '#000000',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '4px'
        }}>Safari</a>
      </div>
    </div>
  );
} else {
  // Render (or hydrate prerendered) application
  const tree = (
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <React.Suspense fallback={<LoadingFallback />}>
              <App />
            </React.Suspense>
          </ErrorBoundary>
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>
  );
  if (container.hasChildNodes()) {
    root = ReactDOM.hydrateRoot(container, tree);
  } else {
    root = ReactDOM.createRoot(container);
    root.render(tree);
  }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Enable hot module replacement in development
if (process.env.NODE_ENV === 'development' && module.hot) {
  module.hot.accept('./App', () => {
    const NextApp = require('./App').default;
    root.render(
      <React.StrictMode>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
              <React.Suspense fallback={<LoadingFallback />}>
                <NextApp />
              </React.Suspense>
            </ErrorBoundary>
          </QueryClientProvider>
        </HelmetProvider>
      </React.StrictMode>
    );
  });
}
